/* New customer offer.

   1. <cart-new-customer-prices> (see snippets/cart-new-customer-offer.liquid)
      Makes the cart drawer and /cart page show the offer the way customers expect:
      - the first pair shows FREE, any further pair shows full price
      - the totals include the 12.00 discount even when the cart has not applied it yet
      - in the mini cart, the red heads-up note is moved above the checkout button

   2. <free-vodka-landing> (see sections/free-vodka-landing.liquid)
      - Add to cart without leaving the page, then opens the cart drawer like the normal add to cart buttons
      - Keeps the card buttons and prices in step with the cart (add, remove or change quantity)

   3. Free vodka product pages (templates/product.free-vodka.json)
      - Keeps the add to cart button, its price and the sticky bar in step with the cart

   Display only. The discount at checkout is what actually sets the price. */
if (!customElements.get('cart-new-customer-prices')) {
  customElements.define('cart-new-customer-prices', class extends HTMLElement {
    connectedCallback() {
      this.apply();
      requestAnimationFrame(() => this.apply());
    }

    apply() {
      let data = null;
      try {
        data = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || 'null');
      } catch (_) {
        return;
      }
      if (!data || !data.lines || !data.lines.length) return;

      const drawer = this.closest('#Cart-Drawer');
      const page = drawer ? null : this.closest('#Cart');
      const scope = drawer || page || document;
      const money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: data.currency || 'GBP' });
      const fmt = cents => money.format(cents / 100);

      if (drawer) {
        const note = drawer.querySelector('[data-nco-note]');
        const buttons = drawer.querySelector('.cart-drawer-buttons');
        if (note && buttons && note.nextElementSibling !== buttons) buttons.before(note);
      }

      data.lines.forEach(line => {
        const row = drawer
          ? scope.querySelector(`#CartDrawerItem-${line.index}`)
          : scope.querySelector(`#CartItem-${line.index}`);
        const price = row && (drawer
          ? row.querySelector('.product-cart-item-price .price')
          : row.querySelector('td.product-subtotal .price'));
        if (!price) return;

        const original = line.unit * line.quantity;
        const final = original - line.free * line.unit;
        const finalLabel = final === 0 ? 'FREE' : fmt(final);
        if (drawer) {
          price.innerHTML = final === original
            ? `<div class="amount main-price">${fmt(original)}</div>`
            : `<div class="amount original-price">${finalLabel}</div><div class="amount compare-price">${fmt(original)}</div>`;
        } else {
          price.innerHTML = final === original
            ? `<ins><span class="amount">${fmt(original)}</span></ins>`
            : `<del><span class="amount">${fmt(original)}</span></del><ins><span class="amount">${finalLabel}</span></ins>`;
        }
        const discounts = row.querySelector('.discounts');
        if (discounts) discounts.remove();
      });

      if (!(data.delta > 0)) return;
      const newTotal = data.total - data.delta;

      if (drawer) {
        const body = scope.querySelector('.cart-drawer-footer table.collaterals tbody');
        if (!body) return;
        const totalRows = [...body.querySelectorAll('tr.cart-total')].filter(row => !row.classList.contains('cart_discounts_ds'));
        const totalRow = totalRows[totalRows.length - 1];
        const totalAmount = totalRow && totalRow.querySelector('.amount');
        if (!totalAmount) return;
        totalAmount.textContent = fmt(newTotal);

        let discountRow = body.querySelector('tr.cart_discounts_ds');
        if (!discountRow) {
          discountRow = totalRow.cloneNode(true);
          discountRow.classList.add('cart_discounts_ds');
          const heading = discountRow.querySelector('th');
          if (heading) heading.textContent = 'Total Discounts';
          totalRow.before(discountRow);
        }
        const discountAmount = discountRow.querySelector('.amount');
        if (discountAmount) discountAmount.textContent = `- ${fmt(data.original - newTotal)}`;
      } else {
        const totalAmount = scope.querySelector('.thb-cart-collaterals tr.cart-total .amount');
        if (totalAmount) totalAmount.textContent = fmt(newTotal);
      }
    }
  });
}

if (!customElements.get('free-vodka-landing')) {
  customElements.define('free-vodka-landing', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.addEventListener('submit', event => this.onSubmit(event), options);
      const sync = () => this.scheduleSync();
      ['cart:item-added', 'line-item:change:end', 'cart:refresh'].forEach(name => document.addEventListener(name, sync, options));
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.syncTimer);
    }

    getSectionInnerHTML(html, selector) {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    async onSubmit(event) {
      const form = event.target.closest('form.fvl__form');
      if (!form) return;
      event.preventDefault();
      if (this.adding) return;
      this.adding = true;
      const button = form.querySelector('button[type="submit"]');
      if (button) {
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
      }

      try {
        const body = new FormData(form);
        body.append('sections', 'cart-drawer,cart-bubble');
        body.append('sections_url', window.location.pathname);
        const response = await fetch(window.theme?.routes?.cart_add_url || '/cart/add', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
          body
        });
        const result = await response.json();
        if (result.status || !result.sections) throw new Error(result.description || 'Unable to add to cart');

        [
          { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
          { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
        ].forEach(item => {
          const host = document.getElementById(item.id);
          if (!host || !result.sections[item.section]) return;
          const target = host.querySelector(item.selector) || host;
          target.innerHTML = this.getSectionInnerHTML(result.sections[item.section], item.selector);
        });
        if (typeof CartDrawer !== 'undefined') new CartDrawer();

        const drawer = document.getElementById('Cart-Drawer');
        if (drawer) {
          document.body.classList.add('open-cc');
          drawer.classList.add('active');
          document.dispatchEvent(new CustomEvent('cart-drawer:open', { detail: {} }));
        }
        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: { product: result.items ? result.items[0] : result }
        }));
      } catch (_) {
        /* Fall back to a normal form post so the shopper is never stuck. */
        form.submit();
        return;
      } finally {
        this.adding = false;
        if (button) {
          button.removeAttribute('aria-busy');
          button.disabled = false;
        }
      }
    }

    scheduleSync() {
      clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => this.sync(), 200);
    }

    async sync() {
      const sectionId = this.dataset.sectionId;
      const grid = this.querySelector('.fvl__grid');
      if (!sectionId || !grid) return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', sectionId);
        const response = await fetch(url.toString());
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.querySelector('.fvl__grid');
        if (fresh) grid.innerHTML = fresh.innerHTML;
      } catch (_) {
        /* Leave the grid as it is, the next cart change tries again. */
      }
    }
  });
}

/* Free vodka product pages: keep the button, its price and the sticky bar in step with the cart.
   The page is re-fetched and only those few elements are copied across, so the gallery and the
   rest of the page are left alone. */
(function () {
  if (window.__ncoPdpSync) return;
  window.__ncoPdpSync = true;

  let timer = null;

  async function syncPdp() {
    if (!document.body.classList.contains('template-product-free-vodka')) return;
    try {
      const response = await fetch(window.location.href, { headers: { Accept: 'text/html' } });
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

      const copy = (selector, mode) => {
        const live = document.querySelector(selector);
        const fresh = doc.querySelector(selector);
        if (!live || !fresh) return;
        if (mode === 'text') live.textContent = fresh.textContent.trim();
        else live.innerHTML = fresh.innerHTML;
      };

      copy('#AddToCart .cw-text-add', 'text');
      copy('#add-to-price-cw', 'html');
      copy('#AddToCartSticky .single-add-to-cart-button--text', 'text');

      const livePrice = document.querySelector('[id^="price-"][id$="--sticky"]');
      const freshPrice = livePrice && doc.getElementById(livePrice.id);
      if (livePrice && freshPrice) livePrice.innerHTML = freshPrice.innerHTML;
    } catch (_) {
      /* Leave the page as it is, the next cart change tries again. */
    }
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(syncPdp, 250);
  }

  ['cart:item-added', 'line-item:change:end', 'cart:refresh'].forEach(name => document.addEventListener(name, schedule));
})();
